import { saveMessage } from "../db/fsDB";
import { fetchAndCachePublicKey } from "./pbKey";
import { encryptDualDm } from "./encrypt";

export async function sendSystemMessage({
  socket,
  myChatId,
  recipientChatId,

  file,
  files,
  senderText,
  recipientText,

  system = false,

  uploadMedia,

  setIsUploading,
  setUploadProgress,

  getUserKey,

  setMessages,
  setUsers,

  showToast,
  uuid,
}) {

  /* ──────────────────────────────
     MULTIPLE MEDIA SUPPORT
     Pass `files` (an array) to send one message per file, sequentially —
     same one-message-per-attachment convention ChatInput/useSendMessage
     use for multi-media sends. `file` (singular) still works as before.
  ────────────────────────────── */
  const fileList = Array.isArray(files) ? files.filter(Boolean) : null;

  if (fileList && fileList.length > 0) {
    const results = [];
    for (const f of fileList) {
      results.push(
        await sendSystemMessage({
          socket,
          myChatId,
          recipientChatId,
          file: f,
          senderText,
          recipientText,
          system,
          uploadMedia,
          setIsUploading,
          setUploadProgress,
          getUserKey,
          setMessages,
          setUsers,
          showToast,
          uuid,
        })
      );
    }
    return fileList.length === 1 ? results[0] : results;
  }

  const msgId = uuid();
  const now = Date.now();

  const chatKey =
    getUserKey(
      recipientChatId
    );

  const isMediaSend =
    !!file;

  const messageType =
    isMediaSend
      ? "media"
      : "text";

  const previewText =
    isMediaSend
      ? "📎 Media"
      : senderText;

  /* ──────────────────────────────
     OPTIMISTIC UPDATE
  ────────────────────────────── */

  const optimisticRecord = {

    id:
      msgId,

    chatKey,

    timestamp:
      now,

    messageType:
      messageType,

    text:
      !isMediaSend
        ? senderText
        : null,

    media:
      isMediaSend
        ? {

            url:
              URL.createObjectURL(
                file
              ),

            type:
              "image",

            mimeType:
              file.type,

            fileName:
              file.name,

            fileSize:
              file.size,

            _optimistic:
              true,

          }
        : null,

    system,

    fromMe:
      true,

    senderChatId:
      myChatId,

  };

  setMessages?.(
    (prev) => ({

      ...prev,

      [chatKey]: [

        ...(prev[
          chatKey
        ] || []),

        optimisticRecord,

      ],

    })
  );

  /* ──────────────────────────────
     SIDEBAR OPTIMISTIC UPDATE
  ────────────────────────────── */

  setUsers?.(
    (prev) =>

      prev.map(
        (u) =>

          String(
            u.chatId
          ) ===
          String(
            recipientChatId
          )

            ? {

                ...u,

                lastMessage:
                  previewText,

                lastActivityAt:
                  now,

              }

            : u

      )

  );

  /* ──────────────────────────────
     PUBLIC KEYS
  ────────────────────────────── */

  const [
    recipientPublicKey,
    senderPublicKey,
  ] = await Promise.all([

    fetchAndCachePublicKey(
      recipientChatId,
      socket
    ),

    fetchAndCachePublicKey(
      myChatId,
      socket
    ),

  ]);

  if (
    !recipientPublicKey ||
    !senderPublicKey
  ) {

    showToast?.(
      "Encryption keys unavailable.",
      "error"
    );

    return null;
  }

  /* ──────────────────────────────
     UPLOAD
  ────────────────────────────── */

  let uploadedMedia =
    null;

  if (
    isMediaSend
  ) {

    setIsUploading?.(
      true
    );

    try {

    uploadedMedia =
  await uploadMedia(
    file,
    {
      msgId,
      type: "message",
      onProgress: (p) =>
        setUploadProgress?.(p),
    }
  );
    } finally {

      setIsUploading?.(
        false
      );

      setUploadProgress?.(
        0
      );

    }

    if (
      !uploadedMedia
    ) {

      showToast?.(
        "Failed to upload payment proof.",
        "error"
      );

      return null;

    }

  }

  /* ──────────────────────────────
     ENCRYPT
  ────────────────────────────── */

  const result =
    await encryptDualDm(

      isMediaSend

        ? JSON.stringify(
            uploadedMedia
          )

        : {

            forRecipient:
              recipientText,

            forSender:
              senderText,

          },

      recipientPublicKey,

      senderPublicKey

    );

  if (!result) {

    showToast?.(
      "Media encryption failed.",
      "error"
    );

    return null;
  }

  const encryptedMediaRecipient =
    result.forRecipient;

  const encryptedMedia =
    result.forSender;

  /* ──────────────────────────────
     SOCKET
  ────────────────────────────── */

  const socketPayload = {

    recipientChatId,

    msgId,

    system,

    ...(isMediaSend

      ? {

          encryptedMedia: {

            forRecipient:
              encryptedMediaRecipient,

            forSender:
              encryptedMedia,

          },

        }

      : {

          encryptedText: {

            forRecipient:
              encryptedMediaRecipient,

            forSender:
              encryptedMedia,

          },

        }

    ),

  };

  socket.emit(
    "send-message",
    socketPayload
  );

  /* ──────────────────────────────
     IDB
  ────────────────────────────── */

  const forIDB = {

    id:
      msgId,

    chatKey,

    timestamp:
      Number(
        now
      ),

    messageType:
      messageType,

    encryptedText:
      !isMediaSend
        ? encryptedMedia
        : null,

    encryptedMedia:
      isMediaSend
        ? encryptedMedia
        : null,

    replyToMessageId:
      null,

    replyPreviewEncrypted:
      null,

    fromMe:
      true,

    senderChatId:
      myChatId,

    recipientChatId,

    system,

  };

  /* ──────────────────────────────
     FINAL RECORD
  ────────────────────────────── */

  const finalRecord = {

    id:
      msgId,

    chatKey,

    timestamp:
      now,

    messageType:
      messageType,

    text:
      !isMediaSend
        ? senderText
        : null,

   media:
  isMediaSend
    ? {
        url:
          uploadedMedia?.url ?? null,

        objectKey:
          uploadedMedia?.objectKey ?? null,

        encrypted:
          uploadedMedia?.encrypted ?? false,

        type: "image",

        mimeType:
          file.type,

        fileName:
          file.name,

        fileSize:
          file.size,
      }
    : null,
    encryptedText:
      !isMediaSend
        ? encryptedMedia
        : null,

    encryptedMedia:
      isMediaSend
        ? encryptedMedia
        : null,

    system,

    fromMe:
      true,

    senderChatId:
      myChatId,

  };

  /* ──────────────────────────────
     REPLACE OPTIMISTIC
  ────────────────────────────── */

  setMessages?.(
    (prev) => ({

      ...prev,

      [chatKey]:
        (
          prev[
            chatKey
          ] || []
        ).map(
          (m) =>

            m.id === msgId
              ? finalRecord
              : m

        ),

    })
  );

  /* ──────────────────────────────
     SIDEBAR FINAL UPDATE
  ────────────────────────────── */

  setUsers?.(
    (prev) =>

      prev.map(
        (u) =>

          String(
            u.chatId
          ) ===
          String(
            recipientChatId
          )

            ? {

                ...u,

                lastMessage:
                  previewText,

                lastActivityAt:
                  now,

              }

            : u

      )

  );

  saveMessage(
    forIDB
  ).catch(() => {});

  return {

    msgId,

    uploadedMedia,

  };

}